import { i18n as I18nInstance } from "i18next";

import { useCallback, useContext, useState } from "react";

import {
  CimodeEnabledContext,
  TranslationNamespace,
  TranslatorFn,
  UiI18nContext,
} from "@plansoft-configuration-renderer/localization";

// Keep in sync with `UI_CONTEXTS` in `src/providers/uiTranslationsProvider.tsx`
// — these are the namespaces we actually fetch translations for.
const UI_CONTEXTS = [
  TranslationNamespace.ConfigurationRenderer,
  TranslationNamespace.CustomApp,
];

function hasAllResourceBundles(instance: I18nInstance): boolean {
  return UI_CONTEXTS.every((ns) =>
    instance.hasResourceBundle(instance.language, ns),
  );
}

/**
 * Drop-in replacement for `@plansoft-configuration-renderer/localization`'s
 * `useUiTranslator()`.
 *
 * That hook binds its `t` function through react-i18next's `useTranslation()`,
 * whose internal snapshot cache (`useSyncExternalStore` backed by a plain
 * `useRef`) is never invalidated when the `i18n` *instance* itself changes
 * identity — only when `ready`/`lng`/`keyPrefix`/`revision` differ from the
 * previous snapshot. `LocalizationProvider` (this app's translation source)
 * creates a brand-new i18next instance every time the backend translations
 * query resolves, but that new instance starts out with the *same* `lng` as
 * the transient, resource-less instance that preceded it — so all of
 * react-i18next's checks spuriously match, and it keeps returning a stale `t`
 * bound to the empty instance forever, for that component, until something
 * else forces it to remount (which is why a Vite HMR reload appeared to
 * "fix" it).
 *
 * We sidestep that cache entirely: `useContext(UiI18nContext)` alone already
 * re-renders correctly on every instance swap (plain React context doesn't
 * have this bug), so we call the instance's own `.t()` directly instead of
 * going through `useTranslation()`.
 *
 * We also keep serving the last instance that actually had its translations
 * loaded until the newly-selected locale's own data arrives (React's
 * "storing information from previous renders" pattern — adjusting state
 * during render, no effect needed: https://react.dev/reference/react/useState#storing-information-from-previous-renders).
 * `LocalizationProvider` swaps in a brand-new, resource-less i18next instance
 * the instant the locale changes — before its fetch has resolved — which
 * would otherwise flash every `t()` call's inline `defaultValue` for a moment
 * on every language switch. Falling back to stale (previous-locale) content
 * instead is the better trade-off here; if a locale's fetch never resolves
 * (e.g. a network error — `useFetchUiTranslations` sets `retry: false`), this
 * keeps showing the previous locale rather than the inline default forever.
 */
export function useUiTranslator(): TranslatorFn {
  const i18n = useContext(UiI18nContext);
  const cimodeEnabled = useContext(CimodeEnabledContext);

  const [lastReadyInstance, setLastReadyInstance] =
    useState<I18nInstance | null>(null);

  const isReady = hasAllResourceBundles(i18n);
  if (isReady && lastReadyInstance !== i18n) {
    setLastReadyInstance(i18n);
  }

  const activeInstance =
    isReady || !lastReadyInstance ? i18n : lastReadyInstance;
  const lng = cimodeEnabled ? "cimode" : activeInstance.language;

  return useCallback(
    (dictionaryKey, defaultValue, options) =>
      activeInstance.t(dictionaryKey, defaultValue, { ...options, lng }),
    [activeInstance, lng],
  );
}
